/**
 * Create validator-bound data objects.
 *
 * ```js
 * // esm
 * import { models } from '@paychex/state';
 *
 * // cjs
 * const { models } = require('@paychex/state');
 *
 * // iife
 * const { models } = window['@paychex/state'];
 *
 * // amd
 * require(['@paychex/state'], function({ models }) { ... });
 * define(['@paychex/state'], function({ models }) { ... });
 * ```
 *
 * @module models
 * @example
 * ```js
 * const operation = {
 *   method: 'GET',
 *   base: 'users',
 *   path: '/users/:id',
 * };
 *
 * const User = validators.object({
 *   loginTime: validators.date('loginTime must be a date'),
 *   username: validators.string('username must be a string'),
 *   displayName: validators.string('displayName must be a string'),
 * }, 'invalid user');
 *
 * export async function getUser(id) {
 *   const request = createRequest(operation, { id });
 *   const response = await fetch(request);
 *   const user = await models.create(response.data, User);
 *   user.on(COMMIT, () => console.log('user data changed'));
 *   return user;
 * }
 * ```
 */

import { get, isEqual, noop } from 'lodash';
import { events, errors } from '@paychex/core';

import { COMMIT, DISPOSE } from '../events';
import utilsFactory from './utils';

import { EventBus } from '@paychex/core/types/events';
import { ValidationError, Validator } from '@paychex/core/types/validators';

/** @ignore */
export const UPDATE = Symbol('__update__');

/** Provides extended functionality for {@link Model} instances */
export const utils = utilsFactory(UPDATE);

/**
 * Represents a schema-backed data object.
 *
 * Users can fire and subscribe to state-related events directly, as the Model
 * extends an {@link https://paychex.github.io/core/EventBus.html EventBus}.
 *
 * ```js
 * const operation = {
 *   method: 'GET',
 *   base: 'users',
 *   path: '/users/:id',
 * };
 *
 * const User = validators.object({
 *   loginTime: validators.date('loginTime must be a date'),
 *   username: validators.string('username must be a string'),
 *   displayName: validators.string('displayName must be a string'),
 * }, 'invalid user');
 *
 * export async function getUser(id) {
 *   const request = createRequest(operation, { id });
 *   const response = await fetch(request);
 *   const user = await models.create(response.data, User);
 *   user.on(Events.COMMIT, () => console.log('user data changed'));
 *   return user;
 * }
 * ```
 * @template T Describes the underlying data structure.
 */
export interface Model<T = any> extends EventBus {

    /**
     * Retrieves a value at the specified location.
     *
     * @param path The path to the value.
     * @param value The default value to use if the path does not resolve to an existing value.
     * @returns The value at the specified path.
     * @throws Throws if the state instance has been disposed.
     * @example
     * ```js
     * const value = model.get(['path', 'to', 'key']);
     * ```
     * @example
     * ```js
     * const cost = model.get(['cart', 'items', 'cost'], 0);
     * ```
     */
    get<K extends keyof T>(path: K | [K]): never | T[K] | undefined;
    get<K extends keyof T, V>(path: K | [K], value: V): never | Exclude<T[K], undefined> | V;
    get<K1 extends keyof T, K2 extends keyof T[K1]>(path: [K1, K2]): never | T[K1][K2] | undefined;
    get<K1 extends keyof T, K2 extends keyof T[K1], V>(path: [K1, K2], value: V): never | Exclude<T[K1][K2], undefined> | V;
    get<K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2]>(path: [K1, K2, K3]): never | T[K1][K2][K3] | undefined;
    get<K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2], V>(path: [K1, K2, K3], value: V): never | Exclude<T[K1][K2][K3], undefined> | V;
    get<K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2], K4 extends keyof T[K1][K2][K3]>(path: [K1, K2, K3, K4]): never | T[K1][K2][K3][K4] | undefined;
    get<K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2], K4 extends keyof T[K1][K2][K3], V>(object: T | null | undefined, path: [K1, K2, K3, K4], value: V): never | Exclude<T[K1][K2][K3][K4], undefined> | V;
    get<V>(object: null | undefined, path: string|string[], value: V): never | V;
    get(object: null | undefined, path: string|string[]): never | undefined;
    get(object: any, path: string|string[], value?: any): never | any;

    /**
     * Creates a frozen copy of the underlying state data.
     *
     * @returns The underlying data in a readonly form.
     * @example
     * ```js
     * const data = model.snapshot();
     * ```
     */
    snapshot(): Readonly<T>

    /**
     * Disposes the state instance, preventing reading from or writing to the underlying data.
     * Fires a Events.DISPOSE event that consumers can listen for to clean up any subscribers.
     *
     * @fires Events.DISPOSE Subscribers can clean up any handles to the state here.
     * @example
     * ```js
     * model.dispose();
     * ```
     */
    dispose(): void

    /**
     * Returns `true` if the model has been disposed; otherwise, returns `false`.
     *
     * @returns `true`, if the model has been disposed.
     * @example
     * ```js
     * if (!model.disposed())
     *   return model.get('path.to.key');
     * ```
     */
    disposed(): boolean

    /** @ignore */
    toJSON(): Readonly<T>

}

export interface ModelUtils {

    /**
     * Wraps a Model instance so its data can be mutated.
     *
     * @template T The type of the underlying data in the wrapped {@link Model} instance.
     * @param model The Model instance to make mutable.
     * @returns An object with methods that can be invoked
     * to mutate the underlying state as part of a transaction.
     * @example
     * ```js
     * const writeable = models.utils.withBatch(model);
     *
     * writeable.on(Events.COMMIT, () => console.log('changed'));
     * writeable.on(Events.ROLLBACK, () => console.log('not changed'));
     *
     * writeable.batch()
     *   .set('key', 'value')
     *   .unset('another.key')
     *   .invoke('arr.push', 1, 2, 3)
     *   .commit()
     *   .then((state) => console.log('current state', state));
     * ```
     * @example
     * ```js
     * // allow a Model instance to be edited
     *
     * const CartItem = validators.object({
     *   sku: validators.string('SKU must be a string'),
     *   description: validators.string('invalid description'),
     *   quantity: validators.number('quantity must be a number'),
     *   cost: validators.number('cost must be numeric'),
     * }, 'cart item is invalid');
     *
     * const Cart = validators.object({
     *   total: validators.number('invalid total'),
     *   items: validators.array(CartItem, 'invalid cart items'),
     * }, 'cart is invalid');
     *
     * const sum = (total, item) => total + (item.cost * item.quantity);
     *
     * export async function getShoppingCart(id) {
     *   const params = { id };
     *   const request = data.createRequest(operation, params);
     *   const response = await data.fetch(request);
     *   const cart = await models.create(response.data, Cart);
     *   // add event listeners
     *   cart.on(Events.COMMIT, (data) => saveChanges(data));
     *   cart.on('add-item', (item) => {
     *     const total = cart.get('items')
     *       .concat(item)
     *       .reduce(sum, 0);
     *     return cart.batch()
     *       .invoke('items.push', item)
     *       .set('total', total)
     *       .commit();
     *   });
     *   // enable batch editing
     *   return models.utils.withBatch(cart);
     * }
     *
     * // consumer:
     * const cart = await getShoppingCart('2add310f');
     * cart.on(Events.COMMIT, (snapshot) => updateUI(snapshot));
     * cart.fire('add-item', {
     *   sku: 'ab-123',
     *   description: 'cell phone',
     *   quantity: 2,
     *   cost: 423.99,
     * });
     * ```
     */
    withBatch<T = any>(model: Model<T>): BatchModel<T>

}

/**
 * Provides batch writing functionality to a {@link Model} instance.
 *
 * @template T Describes the underlying data structure of the given Model.
 */
export interface Batch<T = any> {

    /**
     * Applies all the operations invoked since the last commit.
     *
     * @returns A promise that will be resolved with a snapshot of the updated state data
     * if the transaction succeeds, or rejected with the failure reason if the transaction fails.
     * @fires Events.ROLLBACK Fired if the validator passed to {@link create} throws an exception. Subscribers will be given the Error reason.
     * @fires Events.COMMIT Fired if all the mutations satisfy the validator passed to {@link create}. Subscribers will be provided with a {@link snapshot} of the updated state data.
     * @example
     * ```js
     * model.one(Events.COMMIT, () => console.log('changed'));
     * model.one(Events.ROLLBACK, () => console.log('not changed'));
     * const snapshot = await model.batch()
     *   .set('path.to.key', 'value')
     *   .merge('path.to.obj', { key: 'value' })
     *   .unset('another.key')
     *   .invoke('some.arr.push', 123, 456)
     *   .commit();
     * ```
     */
    commit(): Promise<T>,

    /**
     * Sets the specified value at the given path.
     *
     * @param path The path where the value should be set.
     * @param value The value to set.
     * @returns (batch for chaining)
     * @example
     * ```js
     * model.batch()
     *   .set('path.to.key', 'value')
     *   .commit();
     * ```
     */
    set(path: string|string[], value: any): Batch<T>,

    /**
     * Merges the incoming objects into the object at path.
     *
     * **NOTE:** Arrays in the object will be concatenated.
     *
     * @param path The path to an object where the incoming objects should be merged.
     * @param objects One or more objects to merge into the object at path.
     * @returns (batch for chaining)
     * @example
     * ```js
     * model.batch()
     *   .merge('path.to.object', { a: 123 }, { b: 456 })
     *   .commit();
     * ```
     * @example
     * ```js
     * // arrays will be concatenated
     * model.batch()
     *   .set('path', { arr: [123] })
     *   .merge('path', { arr: [456] })
     *   .commit()
     *   .then(console.log);
     * // { path: { arr: [123, 456] } }
     * ```
     */
    merge(path: string|string[], ...objects: object[]): Batch<T>,

    /**
     * Deletes the value at the specified path.
     *
     * @param path The path whose value should be removed.
     * @returns (batch for chaining)
     * @example
     * ```js
     * model.batch()
     *   .unset('path.to.key')
     *   .commit();
     * ```
     */
    unset(path: string|string[]): Batch<T>,

    /**
     * Invokes the function at the specified path, passing in any arguments given.
     *
     * @param path The path to a function to invoke.
     * @param args The arguments to pass to the invoked function.
     * @returns (batch for chaining)
     * @example
     * ```js
     * model.batch()
     *   .invoke('someArray.push', 'new value')
     *   .commit();
     * ```
     */
    invoke(path: string|string[], ...args: any[]): Batch<T>,
}

/**
 * Represents a {@link Model} instance wrapped in {@link withBatch} to provide {@link Batch} functionality.
 */
export interface BatchModel<T = any> extends Model<T> {

    /**
     * Begins a new batch of edits, wrapping them into a single transaction.
     *
     * @returns A {@link Batch} factory that can be used to write to the wrapped {@link Model} instance.
     * @example
     * ```js
     * const data = await model.batch()
     *   .set('key', 'value')
     *   .merge('path.to.obj', { key: 'value' })
     *   .invoke('some.method', 'arg')
     *   .unset('another.key')
     *   .commit();
     * ```
     */
    batch(): Batch<T>
}

function copy<T = any>(object: T): T {
    return JSON.parse(JSON.stringify(object));
}

/**
 * Creates a new {@link Model} instance with the specified initial data, and
 * ensures the data conforms to the given {@link https://paychex.github.io/core/module-validators.html schema / validator}.
 *
 * @template T The type of the underlying data in the {@link Model} returned.
 * @param data The data used to initialize the state instance.
 * @param schema A {@link https://paychex.github.io/core/module-validators.html validator} that will ensure the data is always correct.
 * @param skipInitialValidation Set to `true` to skip validating the initial data against the provided schema. This is useful when you
 * want to initialize a model that will be populated with data later.
 * @returns A state instance.
 * @example
 * ```js
 * const User = validators.object({
 *   uid: validators.string('uid must be a string'),
 *   roles: validators.array(
 *     validators.oneof(['user', 'admin', 'tester'], 'invalid role'),
 *     'invalid roles array'
 *   ),
 * }, 'invalid user');
 *
 * const user = await models.create({
 *   uid: 'testuser',
 *   roles: ['user', 'tester'],
 * }, User);
 * ```
 */
export async function create<T extends object = any>(
    data: T,
    schema: Validator<ValidationError>,
    skipInitialValidation = false,
): Promise<Model<T>> {

    if (!skipInitialValidation)
        await schema(data);

    let disposed = false;
    let inner = copy(data);

    const bus = events.bus();

    const stop = bus.on(UPDATE, async function onUpdate(draft: T) {
        if (isEqual(draft, inner))
            return;
        await schema(draft);
        inner = copy(draft);
        await bus.fire(COMMIT, snapshot()).catch(noop);
    });

    function at(...args: any[]) {
        if (isDisposed())
            throw errors.error('Cannot read from disposed state.', { path: args[0] });
        return /* eslint "prefer-spread": 0 */ get.apply(null, [inner].concat(args));
    }

    function snapshot(): Readonly<T> {
        return Object.freeze(copy(inner));
    }

    function dispose() {
        if (isDisposed())
            return;
        stop();
        bus.fire(DISPOSE);
        disposed = true;
        inner = null;
    }

    function toJSON() {
        return snapshot();
    }

    function isDisposed(): boolean {
        return disposed;
    }

    return {
        ...bus,
        toJSON,
        get: at,
        snapshot,
        dispose,
        disposed: isDisposed,
    };

}